import { Component } from "@theme/component";
import { mediaHoverFine, prefersReducedMotion } from "@theme/utilities";

const VIEWPORT_PLAY_RATIO = 0.55;

/** @type {Map<Element, VideoCard>} */
const viewportCards = new Map();

/** @type {IntersectionObserver | null} */
let sharedViewportObserver = null;

function getViewportObserver() {
  if (sharedViewportObserver) return sharedViewportObserver;

  sharedViewportObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const card = viewportCards.get(entry.target);
        if (card) card._ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
      }

      let best = null;
      let bestRatio = 0;
      for (const card of viewportCards.values()) {
        if (card._ratio > bestRatio) {
          bestRatio = card._ratio;
          best = card;
        }
      }

      for (const card of viewportCards.values()) {
        if (card === best && bestRatio >= VIEWPORT_PLAY_RATIO) {
          card._activate();
        } else {
          card._deactivate();
        }
      }
    },
    { threshold: [0, 0.25, VIEWPORT_PLAY_RATIO, 0.75, 1] }
  );

  return sharedViewportObserver;
}

class VideoCard extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #hoverIntentTimer = null;

  /** @type {boolean} */
  #active = false;

  /** @type {number} Visible intersection ratio (shared observer). */
  _ratio = 0;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) return;

    if (mediaHoverFine()) {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.addEventListener("mouseenter", this.#onEnter, { signal });
      this.addEventListener("mouseleave", this.#onLeave, { signal });
      this.addEventListener("focusin", this.#onEnter, { signal });
      this.addEventListener("focusout", this.#onFocusOut, { signal });
      return;
    }

    viewportCards.set(this, this);
    getViewportObserver().observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#clearHoverIntent();

    if (!viewportCards.has(this)) return;

    sharedViewportObserver?.unobserve(this);
    viewportCards.delete(this);

    if (viewportCards.size === 0) {
      sharedViewportObserver?.disconnect();
      sharedViewportObserver = null;
    }
  }

  get deferredMedia() {
    return this.querySelector("deferred-media");
  }

  get content() {
    return this.querySelector(".video-card__content");
  }

  /** @internal */
  _activate() {
    if (this.#active) return;
    this.#active = true;
    this.#setActive(true);
    void this.#play();
  }

  /** @internal */
  _deactivate() {
    if (!this.#active) return;
    this.#active = false;
    this.#setActive(false);
    this.#pause();
  }

  #clearHoverIntent() {
    if (!this.#hoverIntentTimer) return;
    window.clearTimeout(this.#hoverIntentTimer);
    this.#hoverIntentTimer = null;
  }

  #onEnter = () => {
    this.#clearHoverIntent();
    this.#hoverIntentTimer = window.setTimeout(() => {
      this.#setActive(true);
      void this.#play();
    }, 120);
  };

  #onLeave = () => {
    this.#clearHoverIntent();
    this.#setActive(false);
    this.#pause();
  };

  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof HTMLElement && this.contains(next)) return;
    this.#onLeave();
  };

  #setActive(isActive) {
    this.classList.toggle("is-hovering", isActive);
    this.content?.classList.toggle("is-hidden", isActive);
  }

  async #play() {
    const dm = this.deferredMedia;
    if (!dm) return;

    if (!dm.hasAttribute("data-media-loaded")) {
      await dm.showDeferredMedia?.();
      return;
    }

    await dm.playMedia?.();
  }

  #pause() {
    this.deferredMedia?.pauseMedia?.();
  }
}

if (!customElements.get("video-card")) {
  customElements.define("video-card", VideoCard);
}
